import {
  email as veeEmailValidator,
  required as veeRequiredValidator,
  min as veeMinValidator,
  max as veeMaxValidator,
} from '@vee-validate/rules'
import { SavedCard } from '@/types/api/api-v3/paymentMethods'

/**
 * Валидация на длину номера телефона
 * @param value - номер телефона
 * @param phoneLength - длина на валидацию
 */
export const phoneRequired = (value: string, phoneLength: number) => {
  if (!value) return 'Поле не заполнено'

  if (value.length !== phoneLength) return 'Некорректный формат номера телефона'

  return true
}

/**
 * Валидация на наличие кода
 * @param value - строка проверки
 */
export const codeValidator = (value: string) => {
  if (!value) {
    return 'Поле не заполнено'
  }

  return true
}

/**
 * Валидация на корректность e-mail
 * @param value - e-mail
 */
export const emailValidator = (value: string) => {
  const latinInvalid = value.match(/[А-ЯЁа-яё]/)

  if (!veeEmailValidator(value) || latinInvalid) {
    return 'Некоpректный email адрес'
  }

  return true
}

/**
 * Валидация на наличие строки
 * @param value - строка проверки
 */
export const requiredValidator = (value: string) => {
  const isNotRequired = ['', null, undefined].includes(value)

  if (!veeRequiredValidator(value) || isNotRequired) {
    return 'Поле не заполнено'
  }

  return true
}

/**
 * Валидация минимальное количество символов в строке
 * @param value - значение на проверку
 * @param length - минимальное количество символов
 */
export const minValidator = (
  value: string | number,
  length: string | number,
) => {
  if (!veeMinValidator(value, { length })) {
    return `Строка должна состоять из ${length} и более символов`
  }

  return true
}

/**
 * Валидация на максимальное количество символов в строке
 * @param value - значение на проверку
 * @param length - максимальное количество символов
 */
export const maxValidator = (value: string | number, length: string | number) =>
  veeMaxValidator(value, { length })

/**
 * Валидация на максимальное количество файлов
 * @param files - массив файлов
 * @param countFiles - максимальное количество файлов
 */
export const maxFiles = (files: File[], countFiles: Number) =>
  files.length > countFiles

/**
 * Валидация на максимальный размер файла
 * @param files - файл
 * @param fileSize - размер файла
 */
export const maxFileSize = (files: File[], fileSize: number) => {
  return !files
    .map((file) => file.size < 1024 * 1024 * fileSize)
    .reduce((a, b) => a && b, true)
}

/**
 * Валидация на максимальный размер списка файлов
 * @param files - список файлов
 * @param filesSize - размер всего списка файлов
 */
export const maxFilesSize = (files: File[], filesSize: number) => {
  const sum = files.reduce((sum, file) => file.size + sum, 0)

  return !(sum < 1024 * 1024 * filesSize)
}

/**
 * Валидация на формат файла
 * @param files - список файлов
 */
export const imageTypes = (files: File[]) => {
  const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/jpg'])

  const isHeic = (string: string): boolean => {
    const match = string.match(/\.[0-9a-z]+$/i)
    if (!match) return false
    return ['.heic', '.heif'].includes(match[0])
  }

  return !files
    .map((file) => allowedTypes.has(file.type) || isHeic(file.name))
    .reduce((a, b) => a && b, true)
}

/**
 * Валидация на Имя пользователя
 * @param str - строка проверки
 */
export const emojiValidator = (str: string) => {
  // https://www.freecodecamp.org/news/how-to-use-regex-to-match-emoji-including-discord-emotes/
  if (/<a?:.+?:\d{18}>|\p{Extended_Pictographic}/gu.test(str)) {
    return 'Имя должно состоять из букв'
  }

  return true
}

/**
 * Валидация на отсутствие выбора способа оплат
 * @param value - строка проверки
 */
export const requiredPaymentMethod = (value: string) => {
  if (!veeRequiredValidator(value)) {
    return 'Укажите способ оплаты'
  }

  return true
}

/**
 * Валидация на работоспособность карты
 * @param card - сохраненная карта
 */
export const notActiveCard = (card: SavedCard) => {
  if (card.is_expired) {
    return 'Выбранная карта неактивна, укажите другой способ оплаты'
  }

  return true
}

export const notActiveGift = (value: number | null) => {
  if (!value && typeof value !== 'number') return 'Выберите подарок'

  return true
}

export const maxNoteLength = (value: string) => {
  if (value && value.length > 300)
    return 'Превышено максимальное количество символов'

  return true
}

export const minPasswordLengthValidator = (value: string) => {
  const minLength = 6

  if (value.length < minLength) {
    return 'Пароль должен состоять из 6 и более символов'
  }

  return true
}
